import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';

const SWIPE_THRESHOLD = 50;

interface UseImageNavigationParams {
  imageCount: number;
  initialIndex: number;
  open: boolean;
}

interface UseImageNavigationReturn {
  currentIndex: number;
  transformRef: React.MutableRefObject<ReactZoomPanPinchRef | null>;
  goNext: () => void;
  goPrev: () => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
}

export const useImageNavigation = ({
  imageCount,
  initialIndex,
  open,
}: UseImageNavigationParams): UseImageNavigationReturn => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isPinching = useRef(false);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const resetZoom = useCallback((): void => {
    transformRef.current?.resetTransform();
  }, []);

  const goToImage = useCallback(
    (index: number): void => {
      if (index < 0 || index >= imageCount) return;
      setCurrentIndex(index);
      resetZoom();
    },
    [imageCount, resetZoom]
  );

  const goNext = useCallback((): void => {
    if (currentIndex < imageCount - 1) {
      goToImage(currentIndex + 1);
    }
  }, [currentIndex, imageCount, goToImage]);

  const goPrev = useCallback((): void => {
    if (currentIndex > 0) {
      goToImage(currentIndex - 1);
    }
  }, [currentIndex, goToImage]);

  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    if (e.touches.length > 1) {
      isPinching.current = true;
      return;
    }
    isPinching.current = false;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent): void => {
      if (isPinching.current) {
        isPinching.current = false;
        return;
      }

      const scale = transformRef.current?.instance?.transformState?.scale ?? 1;
      if (scale > 1) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const dx = touchEndX - touchStartX.current;
      const dy = touchEndY - touchStartY.current;

      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) {
          goNext();
        } else {
          goPrev();
        }
      }
    },
    [goNext, goPrev]
  );

  return {
    currentIndex,
    transformRef,
    goNext,
    goPrev,
    handleTouchStart,
    handleTouchEnd,
  };
};
